import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import safeStorage from '../utils/safeStorage';

const DISMISSED_KEY = 'trial_banner_dismissed_date';

const TrialCountdownBanner = () => {
  const { user } = useAuth();

  // Don't re-render on dismiss — just hide locally until next load
  const [dismissed, setDismissed] = useState(() => {
    try {
      const d = safeStorage.getItem(DISMISSED_KEY);
      if (!d) return false;
      // Re-show after 24h so it reappears as the trial gets closer to ending
      return (Date.now() - parseInt(d, 10)) < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  if (!user || dismissed) return null;

  const { status, trialEnd, plan } = user.subscription || {};
  if (status !== 'trialing' || !trialEnd) return null;

  const msLeft   = new Date(trialEnd).getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

  // Only show when ≤ 5 days left (earlier = too low urgency to be useful)
  if (daysLeft > 5) return null;

  const urgency  = daysLeft <= 1 ? 'danger' : daysLeft <= 3 ? 'warning' : 'info';
  const planLabel = plan === 'family' ? 'Family' : 'Basic';

  const bg = urgency === 'danger'  ? 'linear-gradient(135deg,#dc2626,#b91c1c)' :
             urgency === 'warning' ? 'linear-gradient(135deg,#d97706,#b45309)' :
                                     'linear-gradient(135deg,#2563eb,#1d4ed8)';

  const handleDismiss = () => {
    try { safeStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const dayWord = daysLeft === 1 ? 'day' : 'days';

  return (
    <div
      className="rounded-3 px-4 py-3 mb-3 d-flex align-items-center justify-content-between gap-3 flex-wrap"
      style={{ background: bg, color: '#fff', position: 'relative' }}
    >
      <div className="d-flex align-items-center gap-3">
        <span style={{ fontSize: '22px', flexShrink: 0 }}>
          {urgency === 'danger' ? '⏰' : urgency === 'warning' ? '⚠️' : '🎁'}
        </span>
        <div>
          <div className="fw-bold" style={{ fontSize: '14px' }}>
            {daysLeft === 0
              ? `Your free trial ends today!`
              : `${daysLeft} ${dayWord} left in your free trial`}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.88 }}>
            Upgrade now to keep unlimited access to the {planLabel} plan.
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <Link
          to="/pricing"
          className="btn btn-sm fw-bold rounded-pill px-3"
          style={{
            background: '#fff',
            color: urgency === 'danger' ? '#dc2626' : urgency === 'warning' ? '#b45309' : '#1d4ed8',
            fontSize: '13px',
            border: 'none',
          }}
        >
          Upgrade now →
        </Link>
        <button
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '18px',
            cursor: 'pointer',
            lineHeight: 1,
            padding: '0 4px',
          }}
          aria-label="Dismiss"
        >×</button>
      </div>
    </div>
  );
};

export default TrialCountdownBanner;
